import type { SignatureFont } from '@emr/shared';

/**
 * The four bundled handwriting-style signature fonts, self-hosted under `web/public/fonts/` and
 * declared via `@font-face` in `web/src/styles/signatureFonts.css` — this project's CSP
 * (`font-src 'self'`, `deploy/nginx-emr-demo.conf`) rules out loading them from the Google Fonts
 * CDN the way most sites do, so the actual `.woff2` files are checked into this repo instead.
 *
 * **License: SIL Open Font License, Version 1.1** (https://openfontlicense.org), for all four. Every
 * typeface Google Fonts serves is OFL-licensed (or Apache-2.0, which none of these are), and the
 * OFL explicitly permits redistributing the font files themselves — bundling and self-hosting them
 * here, rather than only linking Google's own stylesheet, is within the license; it does not permit
 * selling the font file on its own or claiming original authorship. Each entry below names the
 * family, its designer of record (per the Google Fonts catalog), and the exact file fetched:
 *
 *   - **Dancing Script**, designed by Pablo Impallari & Igino Marini. `dancing-script-v29-latin-400.woff2`
 *   - **Great Vibes**, designed by TypeSETit (Herculano Nunes). `great-vibes-v21-latin-400.woff2`
 *   - **Caveat**, designed by Impallari Type (Pablo Impallari). `caveat-v23-latin-400.woff2`
 *   - **Sacramento**, designed by Astigmatic (Brian J. Bonislawsky). `sacramento-v17-latin-400.woff2`
 *
 * Only the Latin-subset, regular-weight (400) file for each family was fetched — a signature line
 * is short, and this build has no non-Latin-alphabet requirement today; a further weight or a wider
 * unicode range is a one-file addition to `web/public/fonts/` plus one more `@font-face` block, not
 * a licensing question.
 *
 * The `font-family` values declared in `signatureFonts.css` are prefixed `EMR Signature ...` rather
 * than the bare Google Fonts names, so a signature specifically renders in *this* bundled file and
 * never silently falls through to a same-named font the visitor's OS happens to already have
 * installed at a different (unreviewed) weight or glyph set.
 */
export const SIGNATURE_FONT_OPTIONS: readonly {
  readonly id: SignatureFont;
  readonly label: string;
  readonly cssFontFamily: string;
}[] = [
  {
    id: 'dancing-script',
    label: 'Dancing Script',
    cssFontFamily: "'EMR Signature Dancing Script', cursive",
  },
  {
    id: 'great-vibes',
    label: 'Great Vibes',
    cssFontFamily: "'EMR Signature Great Vibes', cursive",
  },
  { id: 'caveat', label: 'Caveat', cssFontFamily: "'EMR Signature Caveat', cursive" },
  { id: 'sacramento', label: 'Sacramento', cssFontFamily: "'EMR Signature Sacramento', cursive" },
];

/** The font pre-selected in the adoption picker before anyone has chosen anything — the first entry
 * in `SIGNATURE_FONT_OPTIONS`, named as its own constant so a caller never needs an array index
 * (and the non-null assertion indexing one would require) just to seed a default. */
export const DEFAULT_SIGNATURE_FONT: SignatureFont = 'dancing-script';

export function cssFontFamilyFor(fontName: SignatureFont): string {
  const option = SIGNATURE_FONT_OPTIONS.find((candidate) => candidate.id === fontName);
  // Falls back to the closed union's own generic rather than throwing — a note signed under a font
  // id this build no longer lists (the set is closed but not immutable) should still render
  // *something* legible rather than crash the note card it appears on.
  return option?.cssFontFamily ?? 'cursive';
}
